import React, { useState } from 'react';
import { X, Search } from 'lucide-react';
import { APPLIANCE_CATALOG, CATALOG_CATEGORIES } from '../../constants/catalogItems';

/**
 * Gaveta / Drawer Retrátil de Catálogo 2D (Top View)
 * Otimizado para Desktop e Tablets
 */
const ApplianceCatalogDrawer = ({ isOpen, onClose, onSelectItem }) => {
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen) return null;

  const filteredItems = APPLIANCE_CATALOG.filter(item => {
    const matchesCategory = selectedCategory === 'ALL' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleDragStart = (e, item) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'appliance',
      svgId: item.id,
      width: item.defaultWidth,
      height: item.defaultHeight,
      name: item.name
    }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="absolute top-0 left-0 md:left-20 bottom-0 w-80 max-w-[88vw] bg-slate-900 border-r border-slate-800 z-40 shadow-2xl flex flex-col animate-in slide-in-from-left duration-200 select-none">
      {/* Header do Drawer */}
      <div className="p-3 sm:p-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/70">
        <div>
          <h2 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2">
            <span>📦 Catálogo 2D</span>
          </h2>
          <p className="text-[11px] text-slate-400">Clique ou arraste para inserir no croqui</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          title="Fechar catálogo"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Busca e Abas de Categoria */}
      <div className="p-3 border-b border-slate-800 space-y-2.5 bg-slate-900/60">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Buscar eletro, cama, mesa, sofá..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full text-xs pl-8 pr-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Abas com scroll horizontal para Tablet */}
        <div className="flex gap-1 overflow-x-auto pb-1 no-scrollbar">
          {CATALOG_CATEGORIES.map(cat => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap shrink-0 ${
                selectedCategory === cat
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700/80'
              }`}
            >
              {cat === 'ALL' ? 'Todos' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Grid de Itens do Catálogo com scroll vertical */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {filteredItems.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-xs">
            Nenhum item encontrado nesta busca.
          </div>
        ) : (
          filteredItems.map(item => (
            <div
              key={item.id}
              draggable
              onDragStart={(e) => handleDragStart(e, item)}
              onClick={() => onSelectItem(item)}
              className="group p-2.5 sm:p-3 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700/80 hover:border-blue-500/60 cursor-pointer active:scale-[0.98] transition-all flex items-center gap-3 shadow-xs hover:shadow-md"
              title={`Clique para inserir ${item.name}`}
            >
              {/* Miniatura do SVG */}
              <div className="w-14 h-14 bg-white rounded-lg p-1.5 flex items-center justify-center shrink-0 border border-slate-600/50 shadow-inner group-hover:scale-105 transition-transform overflow-hidden">
                {item.renderSvg({ stroke: '#0f172a', strokeWidth: 2, fill: '#ffffff' })}
              </div>

              {/* Informações */}
              <div className="flex-1 min-w-0">
                <h3 className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors truncate">
                  {item.name}
                </h3>
                <p className="text-[11px] font-mono text-slate-400 mt-0.5">
                  {item.defaultWidth} × {item.defaultHeight} cm
                </p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 font-medium">
                    {item.category}
                  </span>
                  <span className="text-[9px] text-emerald-400 font-semibold opacity-90 group-hover:opacity-100 transition-opacity">
                    + Inserir
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Rodapé informativo */}
      <div className="p-2.5 sm:p-3 bg-slate-950/80 border-t border-slate-800 text-[10px] text-slate-400 flex items-center justify-between">
        <span>Toque no item p/ inserir</span>
        <span>Atalho: <strong>R</strong> gira 90°</span>
      </div>
    </div>
  );
};

export default ApplianceCatalogDrawer;
